import { GlobalOptOutRequest } from '@appTypes/GlobalOptOutRequest';
import { GlobalOptOutResponse } from '@appTypes/GlobalOptOutResponse';
import { BulkPrintRequestResource } from '@appTypes/PortfolioBulkActionResource';
import { PrintDataBulk } from '@appTypes/PrintData';
import { ImageSet } from '@appTypes/dealsheet/ImageResource';
import { Contract } from '@appTypes/header/Contract';
import {
  GetAllDataFromPath,
  GetMultiFromPath,
  GetOneFromPathRawResponse,
  Post,
  PostWithReturn,
  Put,
  PutWithReturn,
} from '@services/api/GenericClient';
import { ErrorToast, SuccessToast } from '@services/utils/ToastUtils';
import { CarFaxLocalStorage } from '@services/utils/carfax/car-fax-connect.local-storage';

async function getDealsheet(
  vin: string,
  dealerId: string,
  source?: string,
  turnVin?: string,
  turnDealerId?: string,
) {
  let path = `/dealsheet/dm-dealsheet?vin=${vin}&dealerId=${dealerId}`;

  if (turnVin && turnDealerId) {
    path = `/dealsheet/dm-turn-dealsheet?vin=${vin}&dealerId=${dealerId}&turnVin=${turnVin}&turnDealerId=${turnDealerId}`;
  }

  if (source) {
    path += `&source=${source}`;
  }

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 3000);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getDealsheetPartial(vin: string, dealerId: string) {
  const path = `/dealsheet/dm-dealsheet-partial?vin=${vin}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getDealsheetDealType(vin: string, dealerId: string) {
  const path = `/dealsheet/dealType?vin=${vin}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

 async function getDealsheetDealerId(vin: string) {
  const path = `/dealsheet/dealerid?vin=${vin}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getNotes(closedDealId: string, dealerId: string) {
  const path = `/common/notes/${closedDealId}?dealerId=${dealerId}`;

  return await GetMultiFromPath(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result);
      //   }, 5000);
      // });
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function postNotes(dealerId: string, closedDealId: string, vin: string, noteInput: string) {
  const path = `/common/notes?dealerId=${dealerId}`;

  const noteBody = {
    closedDealId: closedDealId,
    notes: noteInput,
    vin: vin,
  };

  const response = await Post(path, noteBody);
  return response;
}

async function retryNotePush(noteId: string | number, dealerId: string, addMissingData: boolean) {
  const path = `/common/note/${noteId}/retry?dealerId=${dealerId}&addMissingData=${addMissingData}`;

  return await PostWithReturn(path, {}).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getHistory(dealerId: string, vin: string) {
  const path = `/dealsheet/dealhistory?vin=${vin}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putDealStatus(
  isActive: boolean,
  closedDealId: string,
  vin: string,
  dealerId: string,
) {
  const path = `/common/dealstatus?isDealActive=${isActive}&closedDealId=${closedDealId}&vin=${vin}&dealerId=${dealerId}`;

  return await PutWithReturn(path, {}).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putAssignee(
  salespersonId: number,
  closedDealId: string,
  vin: string,
  dealerId: string,
  salepersonName: string,
) {
  const path = `/common/assignee?salespersonId=${salespersonId}&closedDealId=${closedDealId}&vin=${vin}&dealerId=${dealerId}&salepersonName=${salepersonName}`;

  return await PutWithReturn(path, {}).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function postFavorite(item: any, dealerId: string) {
  const path = `/dealsheet/favorite?dealerId=${dealerId}`;

  // saving a favorite returns a 200 with no response,
  // so we return the original item sent to the server
  return await PostWithReturn(path, item).then(
    () => item,
    (error) => {
      ErrorToast(`An error has occurred.`);
      throw Error(error.message);
    },
  );
}

async function postCarFaxReport(query: string, variables: string, accessToken: string) {
  const path = `/common/carFaxReport?query=${query}&variables=${variables}&accessToken=${accessToken}`;

  return await PostWithReturn(path, {}).then(
    (result) => result.json(),
    (error) => {
      ErrorToast(`An error has occurred.`);
      throw Error(error.message);
    },
  );
}

async function getRecall(closedDealId: string, dealerId: string) {
  const path = `/dealsheet/recallDetails?closedDealId=${closedDealId}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 1500);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}
async function putRecalculate(payload: any, dealerId: string, rank) {
  const path = `/dealsheet/dm-recalcuate?dealerId=${dealerId}&rank=${rank}`;

  return await PutWithReturn(path, payload).then(
    (success) => {
      return success.json();
    },
    (error) => {
      ErrorToast(`An error has occurred.`);
      throw Error(error.message);
    },
  );
}
async function putPitchStatus(
  pitchStatusValue: string,
  closedDealId: string,
  vin: string,
  dealerId: string,
  statusCode: number,
  statusDisplayName: string,
  subStatusDisplayName: string,
  surpessErrorToast: boolean,
) {
  const path = `/common/pitchstatus?pitchStatus=${pitchStatusValue}&closedDealId=${closedDealId}&vin=${vin}&dealerId=${dealerId}`;

  // saving a pitch status returns a 200 with no response,
  // so we return the original item sent to the server
  return await PutWithReturn(path, {
    statusCode: statusCode,
    statusDisplayName: statusDisplayName,
    subStatusDisplayName: subStatusDisplayName,
  }).then(
    () => {
      return {
        statusDisplayName: statusDisplayName,
        subStatusDisplayName: subStatusDisplayName,
      };
    },
    (error) => {
      if (!surpessErrorToast) {
        ErrorToast(`An error has occurred.`);
      }
      throw Error(error.message);
    },
  );
}

async function getProductSpecifications(amVehicleIds: Array<string>, dealerId: string) {
  let amVehicleIdsPathPart = '';
  amVehicleIds.forEach((amVehicleId, index) => {
    amVehicleIdsPathPart = `${amVehicleIdsPathPart}amVehicleIds=${amVehicleId}`;
    if (index < amVehicleIds.length - 1) {
      amVehicleIdsPathPart = `${amVehicleIdsPathPart}&`;
    }
  });
  const path = `/dealsheet/productSpecs?${amVehicleIdsPathPart}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 2000);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getLoyalty(customerNumber: string, dealerId: string) {
  const path = `/dealsheet/getLoyalty?customerNumber=${customerNumber}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 1500);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getCampaignActivity(dealerId: string, closedDealId: string) {
  const path = `/dealsheet/campaignActivity?dealerId=${dealerId}&closeddealid=${closedDealId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 1500);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getContract(
  closedDealId: string,
  chromeId: number,
  amVehicleId: string,
  dealerId: string,
) {
  const path = `/dealsheet/contract?id=${closedDealId}&chromeId=${chromeId}&amVehicleId=${amVehicleId}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 1500);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getRebateCategories(brandCode: string, dealerId: string) {
  const path = `/dealsheet/rebateCategories?brandCode=${brandCode}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getWeather(zipCode: string) {
  const path = `/common/weatherInfo?zipCode=${zipCode}`;
  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      // return new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     resolve(result.json());
      //   }, 1500);
      // });
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putCustomerProfile(data: any, dealerId: string) {
  const path = `/dealsheet/dm-customerprofile?dealerId=${dealerId}`;

  return await PutWithReturn(path, data).then(
    (result) => {
      SuccessToast(`Customer Profile Updated.`);
      return result.json();
    },
    (error) => {
      ErrorToast(`An error has occurred.`);
      throw Error(error.message);
    },
  );
}

async function putCommunicationPreference(data: any, dealerId: string) {
  const path = `/dealsheet/communicationpreference?dealerId=${dealerId}`;

  return await PutWithReturn(path, data).then(
    (result) => {
      SuccessToast(`Communication Preference Updated.`);
      return result.json();
    },
    (error) => {
      ErrorToast(`An error has occurred.`);
      throw Error(error.message);
    },
  );
}

async function putContract(data: any, dealerId: string) {
  const path = `/dealsheet/dm-contract?dealerId=${dealerId}`;

  return await PutWithReturn(path, data).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putSaveDiscountMsrps(data: any, dealerId: string) {
  const path = `/dealsheet/dm-saveDiscountMsrp?dealerId=${dealerId}`;

  return await PutWithReturn(path, data).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putSaveApr(data: any, dealerId: string) {
  const path = `/dealsheet/dm-replacementVehicleApr?dealerId=${dealerId}`;

  return await PutWithReturn(path, data).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putRebateCategories(data: any, dealerId: string, rank: string) {
  const path = `/dealsheet/dm-rebateCategories?dealerId=${dealerId}&rank=${rank}`;

  return await PutWithReturn(path, data).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function putMiles(closedDealId: string, vin: string, miles: number, dealerId: string) {
  const path = `/dealsheet/dm-miles?closedDealId=${closedDealId}&vin=${vin}&miles=${miles}&dealerId=${dealerId}`;

  return await Put(path, null).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getAssignee(vin: string, dealerId: string) {
  const path = `/dealsheet/assigneeInfo?vin=${vin}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function postSendToCrm(closedDealId: string, dealerId: string) {
  const path = `/dealsheet/sendToCrm/${closedDealId}?dealerId=${dealerId}`;

  return await PostWithReturn(path, null).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getTrims(vin: string, brandId: number) {
  const path = `/common/trimsByVin?vin=${vin}&brandId=${brandId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function postTrim(
  closedDealId: string,
  vin: string,
  chromeId: number,
  amVehicleId: string,
  dealerId: string,
) {
  const path = `/dealsheet/dm-setDealCid?closedDealId=${closedDealId}&vin=${vin}&cid=${chromeId}&amVehicleId=${amVehicleId}&dealerId=${dealerId}`;

  return await PostWithReturn(path, {}).then(
    (result) => result.json(),
    (error) => {
      ErrorToast(`An error has occurred.`);
      throw Error(error.message);
    },
  );
}

async function UpdateGlobalOptOut(
  dealerId: string,
  body: GlobalOptOutRequest,
): Promise<GlobalOptOutResponse> {
  const apiPath = `/dealsheet/globaloptout?dealerId=${dealerId}`;

  return await PutWithReturn(apiPath, body).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getEstimatorTool(closedDealId: string, mileage: number, dealerId: string) {
  const carFaxConnectAuthToken =
    CarFaxLocalStorage.GetAccessToken() != null ? CarFaxLocalStorage.GetAccessToken() : '';
  const path = `/dealsheet/estimatorTool?closedDealId=${closedDealId}&mileage=${mileage}&authToken=${carFaxConnectAuthToken}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function postCallScript(
  fritzData: any,
  goodToMentionData: any,
  naturalData: any,
  toAvoidData: any,
) {
  const path = `/fritz/talkTrackGeneration/callScript`;
  const payload = {
    ...fritzData,
    bpsDriversPositive: goodToMentionData,
    bpsDriversNeutral: naturalData,
    bpsDriversNegative: toAvoidData,
  };

  return await PostWithReturn(path, payload).then(
    (result) => result.json(),
    (error) => {
      ErrorToast(`An error has occurred while getting call script.`);
      throw Error(error.message);
    },
  );
}

async function postEmailContent(
  fritzData: any,
  goodToMentionData: any,
  naturalData: any,
  toAvoidData: any,
) {
  const path = `/fritz/talkTrackGeneration/email`;
  const payload = {
    ...fritzData,
    bpsDriversPositive: goodToMentionData,
    bpsDriversNeutral: naturalData,
    bpsDriversNegative: toAvoidData,
  };

  return await PostWithReturn(path, payload).then(
    (result) => result.json(),
    (error) => {
      ErrorToast(`An error has occurred while getting Email content.`);
      throw Error(error.message);
    },
  );
}

async function postSmsContent(
  fritzData: any,
  goodToMentionData: any,
  naturalData: any,
  toAvoidData: any,
) {
  const path = `/fritz/talkTrackGeneration/sms`;
  const payload = {
    ...fritzData,
    bpsDriversPositive: goodToMentionData,
    bpsDriversNeutral: naturalData,
    bpsDriversNegative: toAvoidData,
  };

  return await PostWithReturn(path, payload).then(
    (result) => result.json(),
    (error) => {
      ErrorToast(`An error has occurred while getting SMS content.`);
      throw Error(error.message);
    },
  );
}

async function getAllBrands() {
  const path = `/common/brands`;

  return await GetMultiFromPath(path).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getModelsByBrand(brandName: string) {
  const path = `/dealsheet/getModelsByBrand?brandName=${brandName}`;

  return await GetMultiFromPath(path).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getModelImagesByAmVehicleId(amVehicleId: string): Promise<ImageSet[]> {
  const path = `/dealsheet/modelImagesByAmVehicleId?amVehicleId=${amVehicleId}`;

  return await GetMultiFromPath(path).then(
    (result: ImageSet[]) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

async function getMultiVehicleDeals(
  dealerId: string,
  closedDealId: string,
  dealType: number,
  replacementVehicleIds: Array<string>,
  overrides,
): Promise<MultiVehicleDeals> {
  const path = `/dealsheet/dm-multi-vehicle-deals?dealerId=${dealerId}&closedDealId=${closedDealId}`;
  const requestBody = {
    dealType,
    replacementVehicleIds,
    ...overrides,
  };
  const response = await PostWithReturn(path, requestBody).then(
    (result) => {
      return result;
    },
    (error) => {
      throw Error(error.message);
    },
  );

  return await response.json();
}

async function getBulkPrintOfferData(
  deals: BulkPrintRequestResource,
): Promise<Array<PrintDataBulk>> {
  const apiPath = '/dealsheet/bulkprintoffer';

  return await Post<Array<PrintDataBulk>>(apiPath, deals);
}

async function getDeactivatedContract(
  dealerId: string,
  closedDealId: string,
  chromeId: number,
  amVehicleId: string,
): Promise<Contract> {
  const apiPath = `/dealsheet/contract?dealerId=${dealerId}&id=${closedDealId}&chromeId=${chromeId}&amVehicleId=${amVehicleId}`;

  return await GetAllDataFromPath(apiPath);
}

async function getWebsiteActivity(customerNumber: string, dealerId: string) {
  const path = `/dealsheet/shopperActivityModel?customerNumber=${customerNumber}&dealerId=${dealerId}`;

  return await GetOneFromPathRawResponse(path).then(
    (result) => {
      return result.json();
    },
    (error) => {
      throw Error(error.message);
    },
  );
}

export const DealsheetService = {
  getDealsheet,
  getDealsheetPartial,
  getHistory,
  putDealStatus,
  putAssignee,
  getNotes,
  postNotes,
  retryNotePush,
  postFavorite,
  postCarFaxReport,
  getRecall,
  putPitchStatus,
  putRecalculate,
  getProductSpecifications,
  getLoyalty,
  getContract,
  getWeather,
  putCustomerProfile,
  putCommunicationPreference,
  putContract,
  putRebateCategories,
  getCampaignActivity,
  putMiles,
  getAssignee,
  getRebateCategories,
  putSaveDiscountMsrps,
  putSaveApr,
  postSendToCrm,
  getTrims,
  postTrim,
  getDealsheetDealType,
  UpdateGlobalOptOut,
  getEstimatorTool,
  postCallScript,
  postEmailContent,
  postSmsContent,
  getBulkPrintOfferData,
  getDeactivatedContract,
  getWebsiteActivity,
  getModelsByBrand,
  getModelImagesByAmVehicleId,
  getMultiVehicleDeals,
  getAllBrands,
  getDealsheetDealerId
};
